import { RedirectRequest } from '@azure/msal-browser';
import { ClientConfig } from '../../../../clientConfig';
import { AUTO_INSTANT_ACTIVATION_PARAMS, OPENID_PARAMS } from '../../../../types/openid_enum';
import { buildBaseAuthenticationRequest } from '../../../util/buildAuthenticationRequest';

// AIA can only continue into protected PIP routes that are part of its own flow. Keeping this as
// paths rather than full URLs avoids turning the selectEmail response into an open redirect surface.
const AUTO_INSTANT_ACTIVATION_AUTHENTICATED_PATHS = ['/autoinstantactivation', '/register'];

type BuildAutoInstantActivationMsalRequestParams = {
    brandInfo: ClientConfig['brandConfig'];
    correlationId: string;
    redirectStartPage: string;
    scopes: string[];
    searchParams: URLSearchParams;
};

/**
 * Returns the protected AIA route MSAL should load after interactive authentication.
 *
 * Unexpected paths return undefined so the caller can fail closed before invoking MSAL.
 */
export function buildAutoInstantActivationRedirectStartPage(redirectPath: string, redirectParams: URLSearchParams): string | undefined {
    if (!redirectPath || !AUTO_INSTANT_ACTIVATION_AUTHENTICATED_PATHS.includes(redirectPath)) {
        return undefined;
    }

    return `${redirectPath}?${redirectParams.toString()}`;
}

/**
 * Builds the forced interactive MSAL request used after AIA email selection.
 */
export function buildAutoInstantActivationAuthenticationRequest({
    brandInfo,
    correlationId,
    redirectStartPage,
    scopes,
    searchParams,
}: BuildAutoInstantActivationMsalRequestParams): RedirectRequest {
    const msalRedirectRequest = buildBaseAuthenticationRequest({ brandInfo, correlationId, scopes, searchParams });
    const extraQueryParameters = msalRedirectRequest.extraQueryParameters!;

    // AIA must always show an interactive sign-in/sign-up screen after email selection.
    // Otherwise a cached MSAL account from a shared browser can be used before B2C prompts.
    msalRedirectRequest.prompt = 'login';
    msalRedirectRequest.redirectStartPage = redirectStartPage;
    extraQueryParameters[AUTO_INSTANT_ACTIVATION_PARAMS.IS_AIA_LINK_EXPIRED] = 'false';

    if (searchParams.get('is_signup_enabled')) {
        extraQueryParameters.is_signup_enabled = searchParams.get('is_signup_enabled') as string;
    }
    if (searchParams.get(OPENID_PARAMS.AMPLITUDE_DEVICE_ID)) {
        extraQueryParameters[OPENID_PARAMS.AMPLITUDE_DEVICE_ID] = searchParams.get(OPENID_PARAMS.AMPLITUDE_DEVICE_ID) as string;
    }
    if (searchParams.get(OPENID_PARAMS.AMPLITUDE_SESSION_ID)) {
        extraQueryParameters[OPENID_PARAMS.AMPLITUDE_SESSION_ID] = searchParams.get(OPENID_PARAMS.AMPLITUDE_SESSION_ID) as string;
    }

    return msalRedirectRequest;
}
